import * as React from 'react';
import {Grid} from '@mui/material';
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridApi,
  gridDetailPanelExpandedRowsContentCacheSelector,
  GridRenderCellParams,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningIcon from '@mui/icons-material/Warning';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import {ReactComponent as EciEnabledIcon} from '../../../../assests/svg/investigative.svg';
import {ReactComponent as HighlyConfidentialIcon} from '../../../../assests/svg/highly-confidential.svg';
import {t} from 'i18next';
import {AurDataGridColDef} from '../../../../types/types';
import AurDataGridCellWrapper from '../../../common/aur-data-grid/AurDataGridCellWrapper';
import FieldPopoverGridFilter, {
  FieldFilterGridData,
} from '../../field-management/field-table/FieldPopoverGridFilter';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';
import AurIconButton from '../../../common/aur-button/AurIconButton';
import AurCountryFlag from '../../../common/aur-country-flag/AurCountryFlag';
import {AurDataGridFilterState} from '../../../common/aur-data-grid-pro/useServerSideDataGrid';
import FilterBooleanListField from '../../../common/aur-data-grid-pro/FilterBooleanListField';
import {IFilterData} from '../../../../types/client-management/cientManagementTypes';

const getIconOptions = () => {
  const options: FieldFilterGridData[] = [];

  options.push({
    id: 1,
    value: 'ecaEnabled',
    label: t('welcomeScreenMultiClient.projectTable.header.icons.isEciEnabled'),
  });

  options.push({
    id: 2,
    value: 'hasAlerts',
    label: t('welcomeScreenMultiClient.projectTable.header.icons.hasAlerts'),
  });

  options.push({
    id: 3,
    value: 'highlyConfidential',
    label: t(
      'welcomeScreenMultiClient.projectTable.header.icons.isHighlyConfidential'
    ),
  });

  return options;
};

const getDefaultIconOptions = (
  cachedState: AurDataGridFilterState | null | undefined
) => {
  const options: string[] = getIconOptions().map((i) => i.value ?? '');
  const defaultOptions: string[] = [];

  for (const option of options) {
    const cachedValue = cachedState?.searchState?.items?.find(
      (i) => i.field === option
    );
    if (cachedValue) {
      defaultOptions.push(option);
    }
  }

  return defaultOptions;
};

const getCountryOptions = (filterData: IFilterData | null | undefined) => {
  const options: FieldFilterGridData[] = [];

  if (!filterData || !filterData.countries) {
    return options;
  }

  let index = 1;
  for (const country of filterData.countries) {
    options.push({
      id: index++,
      label: country.countryName,
      value: country.countryCode,
    });
  }
  return options;
};

const getDefaultCountryOptions = (
  filterData: IFilterData | null | undefined,
  cachedState: AurDataGridFilterState | null | undefined
) => {
  const options: FieldFilterGridData[] = getCountryOptions(filterData);
  const defaultOptions: (string | number)[] = [];

  const cachedField = cachedState?.searchState?.items?.find(
    (i) => i.field === 'domainCountryCode'
  );
  if (!cachedField) {
    return [];
  }

  for (const option of options) {
    const cachedValue = (cachedField.value as string[]).find(
      (i) => i === option.value
    );
    if (cachedValue) {
      defaultOptions.push(option.id);
    }
  }

  return defaultOptions;
};

const renderExpandCollapseCell = (params: GridRenderCellParams) => {
  // https://v5.mui.com/x/react-data-grid/master-detail/#customizing-the-detail-panel-toggle
  const {id, value: isExpanded} = params;
  const apiRef = useGridApiContext();

  // To avoid calling ´getDetailPanelContent` all the time, the following selector
  // gives an object with the detail panel content for each row id.
  const contentCache = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowsContentCacheSelector
  );

  // If the value is not a valid React element, it means that the row has no detail panel.
  const hasDetail = React.isValidElement(contentCache[id]);

  return (
    <AurDataGridCellWrapper
      cellData=""
      className="project-view_expand-collapse_cell"
      customCellContent={
        <AurIconButton
          size="small"
          disabled={!hasDetail}
          ariaLabel={isExpanded ? 'Close' : 'Open'}
          icon={
            isExpanded ? (
              <ExpandLessIcon aria-hidden="true" />
            ) : (
              <ExpandMoreIcon aria-hidden="true" />
            )
          }
        />
      }
    />
  );
};

const renderCountryFlag = (params: GridRenderCellParams) => {
  const countryName = params.value;
  const countryCode = params.row.domainCountryCode;

  if (!countryName) {
    return <AurDataGridCellWrapper cellData="" tooltipDisabled />;
  }

  const cellContent = <AurCountryFlag countryCode={countryCode} altText="" />;

  return (
    <AurDataGridCellWrapper
      cellData=""
      tooltipText={countryName}
      customCellContent={cellContent}
    />
  );
};

const renderIcons = (params: GridRenderCellParams) => {
  const isEciEnabled = params.row.ecaEnabled;
  const hasAlerts = params.row.hasAlerts;
  const isHighlyConfidential = params.row.highlyConfidential;

  const cellContent = (
    <Grid
      container
      direction="row"
      spacing={0.5}
      sx={{
        justifyContent: 'flex-end',
        paddingRight: '16px',
      }}
    >
      {isHighlyConfidential && (
        <Grid item>
          <AurLightTooltip
            title={t(
              'welcomeScreenMultiClient.projectTable.header.icons.isHighlyConfidential'
            )}
          >
            <HighlyConfidentialIcon
              className="project-view_hc-icon"
              data-testid="project-view_hc-icon"
            />
          </AurLightTooltip>
        </Grid>
      )}

      {isEciEnabled && (
        <Grid item>
          <AurLightTooltip
            title={t(
              'welcomeScreenMultiClient.projectTable.header.icons.isEciEnabled'
            )}
          >
            <EciEnabledIcon
              className="project-view_eci-icon"
              data-testid="project-view_eci-icon"
            />
          </AurLightTooltip>
        </Grid>
      )}

      {hasAlerts && (
        <Grid item>
          <AurLightTooltip
            title={t(
              'welcomeScreenMultiClient.projectTable.header.icons.hasAlerts'
            )}
          >
            <WarningIcon
              fontSize="small"
              className="project-view_alerts-icon"
              data-testid="project-view_alerts-icon"
            />
          </AurLightTooltip>
        </Grid>
      )}
    </Grid>
  );

  return <AurDataGridCellWrapper cellData="" customCellContent={cellContent} />;
};

const renderClientName = (
  params: GridRenderCellParams,
  pinnedState: Record<string, boolean>,
  onPin: (projectId: string) => void,
  onUnin: (projectId: string) => void
) => {
  const projectId = String(params.row.projectId ?? params.id);
  const isPinned = pinnedState[projectId] ?? params.row.pinned;

  const cellContent = (
    <Grid
      container
      direction="row"
      spacing={0.5}
      sx={{alignItems: 'center'}}
      className="project-view_client-name_cell"
    >
      <Grid item className="project-view_client-pin">
        <AurLightTooltip
          title={t(
            isPinned
              ? 'welcomeScreenMultiClient.projectTable.unpinTooltip'
              : 'welcomeScreenMultiClient.projectTable.pinTooltip'
          )}
        >
          <div>
            <AurIconButton
              size="small"
              ariaLabel={t(
                isPinned
                  ? 'welcomeScreenMultiClient.projectTable.unpinTooltip'
                  : 'welcomeScreenMultiClient.projectTable.pinTooltip'
              )}
              icon={
                isPinned ? (
                  <PushPinIcon aria-hidden="true" />
                ) : (
                  <PushPinOutlinedIcon aria-hidden="true" />
                )
              }
              onClick={() =>
                isPinned ? onUnin?.(projectId) : onPin?.(projectId)
              }
              className={`project-view_pin-icon ${isPinned ? 'show' : ''}`}
              dataTestId="project-view_pin-icon"
            />
          </div>
        </AurLightTooltip>
      </Grid>
      <Grid item className="project-view_client-name">
        {params.value}
      </Grid>
    </Grid>
  );

  return <AurDataGridCellWrapper cellData="" customCellContent={cellContent} />;
};

const renderTableCell = (params: GridRenderCellParams) => {
  return (
    <AurDataGridCellWrapper
      cellData={params.value}
      tooltipText={params.value}
      tooltipDisabled={!params.value}
    />
  );
};

const renderGbTableCell = (params: GridRenderCellParams) => {
  let text = params.value;
  if (params.value) {
    text = `${params.value} GB`;
  }

  return (
    <AurDataGridCellWrapper
      cellData={text}
      tooltipText={text}
      tooltipDisabled={!params.value}
    />
  );
};

const renderEmptyHeaderFilter = () => {
  return <span className="sr-only">{t('common.noFilter')}</span>;
};

export const getProjectTableColumnDef = (
  apiRef: React.MutableRefObject<GridApi>,
  cachedState: AurDataGridFilterState | null | undefined,
  filterData: IFilterData | null | undefined,
  pinnedState: Record<string, boolean>,
  onPin: (projectId: string) => void,
  onUnin: (projectId: string) => void
): AurDataGridColDef[] => [
  {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    headerName: 'detailsCollapse',
    renderHeader: () => <span className="sr-only">detailsCollapse</span>,
    minWidth: 48,
    renderCell: renderExpandCollapseCell,
    renderHeaderFilter: renderEmptyHeaderFilter,
  },
  {
    field: 'clientName',
    headerName: t('welcomeScreenMultiClient.projectTable.header.clientName'),
    headerClassName: 'project-view_header',
    minWidth: 180,
    sortable: true,
    filterable: true,
    renderCell: (params) =>
      renderClientName(params, pinnedState, onPin, onUnin),
  },
  {
    field: 'projectName',
    headerName: t('welcomeScreenMultiClient.projectTable.header.projectName'),
    headerClassName: 'project-view_header',
    minWidth: 262,
    sortable: true,
    filterable: true,
    renderCell: renderTableCell,
  },
  {
    field: 'icons',
    minWidth: 117,
    headerName: t('welcomeScreenMultiClient.projectTable.header.status'),
    headerClassName: 'project-view_header',
    sortable: false,
    renderCell: renderIcons,
    renderHeaderFilter: () => (
      <FilterBooleanListField
        key={'icons'}
        columnField={'icons'}
        apiRef={apiRef}
        dataArray={getIconOptions()}
        defaultOptions={getDefaultIconOptions(cachedState)}
        variant={'outlined'}
        useDefaultLabel={true}
        useDefaultPlaceholder={false}
        selectionType="single"
        inputType="text"
        inputClass="filter-input-field-container"
        caretIconContainerClass="caret-dropdown-icon"
        dataTestId="project-view_icons-filter"
      />
    ),
  },
  {
    field: 'engagementCode',
    headerName: t(
      'welcomeScreenMultiClient.projectTable.header.engagementCode'
    ),
    headerClassName: 'project-view_header',
    minWidth: 186,
    sortable: true,
    filterable: true,
    renderCell: renderTableCell,
  },
  {
    field: 'alphaCode',
    headerName: t('welcomeScreenMultiClient.projectTable.header.alphaCode'),
    headerClassName: 'project-view_header',
    minWidth: 148,
    sortable: true,
    filterable: true,
    renderCell: renderTableCell,
  },
  {
    field: 'clientPreferredId',
    headerName: t(
      'welcomeScreenMultiClient.projectTable.header.clientPreferredId'
    ),
    headerClassName: 'project-view_header',
    minWidth: 192,
    sortable: true,
    filterable: true,
    renderCell: renderTableCell,
  },
  {
    field: 'domainCountryCode',
    minWidth: 129,
    headerName: t(
      'welcomeScreenMultiClient.projectTable.header.domainCountryCode'
    ),
    headerClassName: 'project-view_header',
    sortable: true,
    renderCell: renderCountryFlag,
    renderHeaderFilter: () => (
      <FieldPopoverGridFilter
        key={'domainCountryCode'}
        columnField={'domainCountryCode'}
        type={'options'}
        apiRef={apiRef}
        dataArray={getCountryOptions(filterData)}
        defaultArray={getDefaultCountryOptions(filterData, cachedState)}
        variant={'outlined'}
        useDefaultLabel={true}
        useDefaultPlaceholder={false}
        selectionType="single"
        inputType="text"
        caretIconContainerClass="caret-dropdown-icon"
        dataTestId="project-view_country-filter"
      />
    ),
  },
  {
    field: 'branchName',
    headerName: t('welcomeScreenMultiClient.projectTable.header.branchName'),
    headerClassName: 'project-view_header',
    minWidth: 200,
    sortable: true,
    filterable: true,
    renderCell: renderTableCell,
  },
  {
    field: 'departmentName',
    headerName: t(
      'welcomeScreenMultiClient.projectTable.header.departmentName'
    ),
    headerClassName: 'project-view_header',
    minWidth: 190,
    sortable: true,
    filterable: true,
    renderCell: renderTableCell,
  },
  {
    field: 'dataDeliveriesCount',
    headerName: t(
      'welcomeScreenMultiClient.projectTable.header.dataDeliveries'
    ),
    headerClassName: 'project-view_header',
    minWidth: 136,
    sortable: false,
    filterable: false,
    renderCell: renderTableCell,
    renderHeaderFilter: renderEmptyHeaderFilter,
  },
  // {
  //   field: 'dataProcessingGb',
  //   headerName: t('welcomeScreenMultiClient.projectTable.header.datasetsProcessing'),
  //   headerClassName: 'project-view_header',
  //   minWidth: 145,
  //   sortable: false,
  //   filterable: false,
  //   renderCell: renderTableCell,
  // },
  {
    field: 'processedDatasetsGb',
    headerName: t(
      'welcomeScreenMultiClient.projectTable.header.processedDatasets'
    ),
    headerClassName: 'project-view_header',
    minWidth: 137,
    sortable: false,
    filterable: false,
    renderCell: renderTableCell,
    renderHeaderFilter: renderEmptyHeaderFilter,
  },
  {
    field: 'productionsCompletedCount',
    headerName: t(
      'welcomeScreenMultiClient.projectTable.header.productionsCompleted'
    ),
    headerClassName: 'project-view_header',
    minWidth: 152,
    sortable: false,
    filterable: false,
    renderCell: renderTableCell,
    renderHeaderFilter: renderEmptyHeaderFilter,
  },
  {
    field: 'reviewWorkspaceUsersCount',
    headerName: t(
      'welcomeScreenMultiClient.projectTable.header.reviewWorkspaceUsers'
    ),
    headerClassName: 'project-view_header',
    minWidth: 193,
    sortable: false,
    filterable: false,
    renderCell: renderTableCell,
    renderHeaderFilter: renderEmptyHeaderFilter,
  },
  {
    field: 'totalDataUnderManagementGb',
    headerName: t(
      'welcomeScreenMultiClient.projectTable.header.totalDataUnderManagement'
    ),
    headerClassName: 'project-view_header',
    minWidth: 202,
    sortable: false,
    filterable: false,
    renderCell: renderGbTableCell,
    renderHeaderFilter: renderEmptyHeaderFilter,
  },
  {
    field: 'highlyConfidential',
    headerName: 'highlyConfidential',
    headerClassName: 'project-view_header',
    minWidth: 0,
    sortable: false,
    filterable: false,
    renderCell: renderTableCell,
    renderHeaderFilter: renderEmptyHeaderFilter,
  },
  {
    field: 'ecaEnabled',
    headerName: 'ecaEnabled',
    headerClassName: 'project-view_header',
    minWidth: 0,
    sortable: false,
    filterable: false,
    renderCell: renderTableCell,
    renderHeaderFilter: renderEmptyHeaderFilter,
  },
  {
    field: 'hasAlerts',
    headerName: 'hasAlerts',
    headerClassName: 'project-view_header',
    minWidth: 0,
    sortable: false,
    filterable: false,
    renderCell: renderTableCell,
    renderHeaderFilter: renderEmptyHeaderFilter,
  },
];
